import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shipmentsApi } from '../../api/shipments-api';
import { ApiError } from '../../api/errors';
import {
  evidenceKindLabel,
  evidenceSourceLabel,
  type Shipment,
  type TrackingRefreshResult,
} from '../../domain/shipment';

function formatTime(value: string | undefined): string {
  return value ? new Date(value).toLocaleString() : 'Time not reported';
}

function refreshSummary(result: TrackingRefreshResult): string {
  if (result.errors.length > 0) return 'Some tracking could not be checked. Try again later.';
  if (result.evidenceRecorded === 0) return 'No new tracking events.';
  const events = `${result.evidenceRecorded} new tracking event${result.evidenceRecorded === 1 ? '' : 's'}`;
  return result.deliveriesRecorded > 0 ? `${events}, including delivery.` : `${events}.`;
}

/**
 * Carrier evidence for a shipment (tidaltech/koi#8086): when Cargonizer
 * handed it to the carrier and, for Bring, the carrier's own events. Evidence
 * only ever adds information; it doesn't change Item status.
 */
export function ShipmentTrackingPanel({ shipment }: { shipment: Shipment }) {
  const queryClient = useQueryClient();
  const evidence = useQuery({
    queryKey: ['shipment-evidence', shipment.id],
    queryFn: () => shipmentsApi.listShipmentEvidence(shipment.id),
  });
  const refresh = useMutation({
    mutationFn: () => shipmentsApi.refreshShipmentTracking([shipment.id]),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shipment-evidence', shipment.id] }),
        queryClient.invalidateQueries({ queryKey: ['shipment', shipment.id] }),
        queryClient.invalidateQueries({ queryKey: ['shipments'] }),
      ]);
    },
  });

  const bookedInCargonizer = Boolean(shipment.cargonizerConsignmentNumber);

  return (
    <section className="site-card" aria-label="Tracking">
      <div className="site-card-heading">
        <h2>Tracking</h2>
        {bookedInCargonizer ? (
          <button type="button" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
            {refresh.isPending ? 'Checking…' : 'Refresh tracking'}
          </button>
        ) : null}
      </div>

      {refresh.isSuccess ? (
        <p role="status" className="drawer-version-note">
          {refreshSummary(refresh.data)}
          {refresh.data.bringEnabled
            ? ''
            : ' Carrier events from Bring aren’t available yet; only the hand-off from Cargonizer is shown.'}
        </p>
      ) : null}
      {refresh.isError ? (
        <p role="alert" className="form-error">
          {refresh.error instanceof ApiError && refresh.error.code === 'FORBIDDEN'
            ? 'You do not have permission to refresh tracking.'
            : 'Tracking could not be refreshed. Try again in a few minutes.'}
        </p>
      ) : null}

      {evidence.isLoading ? <p className="empty-note">Loading tracking…</p> : null}
      {evidence.isError ? (
        <p role="alert" className="form-error">
          Tracking could not be loaded.{' '}
          <button type="button" onClick={() => void evidence.refetch()}>
            Retry
          </button>
        </p>
      ) : null}
      {evidence.isSuccess && evidence.data.length === 0 ? (
        <p className="empty-note">
          {bookedInCargonizer
            ? 'No tracking events recorded yet.'
            : 'Tracking is only available for shipments booked through Cargonizer.'}
        </p>
      ) : null}
      {evidence.isSuccess && evidence.data.length > 0 ? (
        <ol className="tracking-timeline">
          {evidence.data.map((event) => (
            <li key={event.id}>
              <div className="tracking-event-heading">
                <strong>{evidenceKindLabel(event.kind)}</strong>
                <span className="state-badge">{evidenceSourceLabel(event.source)}</span>
                {event.confirmation === 'INFERRED' ? (
                  <span className="attention-badge">Inferred</span>
                ) : null}
              </div>
              <span className="tracking-event-meta">
                {formatTime(event.eventTime)}
                {event.eventTimeApproximate ? ' (approximate)' : ''}
                {event.location ? ` · ${event.location}` : ''}
              </span>
              {event.description ? <span>{event.description}</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
